import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';

const DevisSection = () => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    typeTransport: '',
    destination: '',
    volume: '',
    description: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Écouter les changements de destination depuis l'URL ou les événements
  useEffect(() => {
    const handleDestinationUpdate = () => {
      const destinationInput = document.getElementById('destination') as HTMLInputElement;
      if (destinationInput && destinationInput.value && destinationInput.value !== formData.destination) {
        setFormData(prev => ({ ...prev, destination: destinationInput.value }));
      }
    };

    // Vérifier après un court délai pour laisser le temps à la navigation
    const timer = setTimeout(handleDestinationUpdate, 100);
    return () => clearTimeout(timer);
  }, [location]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('https://submit-form.com/v6tBnl90i', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nom: '',
          email: '',
          telephone: '',
          typeTransport: '',
          destination: '',
          volume: '',
          description: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Erreur:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="devis" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block bg-accent/10 text-accent px-4 py-2 rounded-full text-xs md:text-sm font-semibold mb-4">
              DEMANDE DE DEVIS
            </span>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-4 md:mb-6">
              Obtenez Votre Devis Personnalisé en 24h
            </h2>
            <p className="text-gray-600 text-base md:text-lg mb-6 md:mb-8 leading-relaxed">
              Remplissez notre formulaire détaillé et recevez un devis complet adapté à vos besoins spécifiques. Notre équipe d'experts en logistique internationale analysera votre demande pour vous proposer la solution la plus optimale et économique.
            </p>

            <div className="space-y-4 md:space-y-6">
              <div className="flex items-start space-x-3 md:space-x-4">
                <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-accent/10 rounded-lg flex-shrink-0">
                  <i className="ri-check-line text-xl md:text-2xl text-accent"></i>
                </div>
                <div>
                  <h4 className="font-bold text-primary mb-1 text-sm md:text-base">Devis Gratuit et Sans Engagement</h4>
                  <p className="text-gray-600 text-xs md:text-sm">Recevez une estimation détaillée et transparente de tous les coûts liés à votre expédition, sans aucune obligation de votre part.</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 md:space-x-4">
                <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-accent/10 rounded-lg flex-shrink-0">
                  <i className="ri-time-line text-xl md:text-2xl text-accent"></i>
                </div>
                <div>
                  <h4 className="font-bold text-primary mb-1 text-sm md:text-base">Réponse Rapide Garantie</h4>
                  <p className="text-gray-600 text-xs md:text-sm">Notre équipe vous répond sous 24 heures maximum avec un devis complet et des conseils personnalisés pour optimiser votre transport.</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 md:space-x-4">
                <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-accent/10 rounded-lg flex-shrink-0">
                  <i className="ri-shield-check-line text-xl md:text-2xl text-accent"></i>
                </div>
                <div>
                  <h4 className="font-bold text-primary mb-1 text-sm md:text-base">Expertise et Conseils Personnalisés</h4>
                  <p className="text-gray-600 text-xs md:text-sm">Bénéficiez de l'expertise de nos spécialistes en logistique africaine pour choisir la meilleure solution de transport adaptée à vos marchandises.</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 md:space-x-4">
                <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-accent/10 rounded-lg flex-shrink-0">
                  <i className="ri-price-tag-3-line text-xl md:text-2xl text-accent"></i>
                </div>
                <div>
                  <h4 className="font-bold text-primary mb-1 text-sm md:text-base">Tarifs Compétitifs</h4>
                  <p className="text-gray-600 text-xs md:text-sm">Profitez de nos tarifs négociés avec nos partenaires pour obtenir le meilleur rapport qualité-prix sur toutes vos expéditions vers l'Afrique.</p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 md:p-8 shadow-2xl border border-gray-100"
          >
            <div className="mb-6">
              <h3 className="text-xl md:text-2xl font-bold text-primary mb-2">Formulaire de Demande</h3>
              <p className="text-gray-600 text-xs md:text-sm">Tous les champs marqués d'un * sont obligatoires</p>
            </div>

            <form id="devis-form" data-readdy-form onSubmit={handleSubmit}>
              <div className="space-y-4 md:space-y-5">
                <div>
                  <label htmlFor="nom" className="block text-xs md:text-sm font-semibold text-gray-700 mb-2">
                    Nom Complet *
                  </label>
                  <input
                    type="text"
                    id="nom"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    required
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent text-sm transition-all"
                    placeholder="Votre nom complet"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email" className="block text-xs md:text-sm font-semibold text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-3 md:px-4 py-2.5 md:py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent text-sm transition-all"
                      placeholder="votre@email.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="telephone" className="block text-xs md:text-sm font-semibold text-gray-700 mb-2">
                      Téléphone *
                    </label>
                    <input
                      type="tel"
                      id="telephone"
                      name="telephone"
                      value={formData.telephone}
                      onChange={handleChange}
                      required
                      className="w-full px-3 md:px-4 py-2.5 md:py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent text-sm transition-all"
                      placeholder="+33 6 12 34 56 78"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="typeTransport" className="block text-xs md:text-sm font-semibold text-gray-700 mb-2">
                      Type de Transport *
                    </label>
                    <select
                      id="typeTransport"
                      name="typeTransport"
                      value={formData.typeTransport}
                      onChange={handleChange}
                      required
                      className="w-full px-3 md:px-4 py-2.5 md:py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent text-sm transition-all"
                    >
                      <option value="">Sélectionner un type</option>
                      <option value="aerien">Transport Aérien</option>
                      <option value="maritime">Transport Maritime</option>
                      <option value="groupage">Groupage de Marchandises</option>
                      <option value="express">Service Express</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="destination" className="block text-xs md:text-sm font-semibold text-gray-700 mb-2">
                      Destination *
                    </label>
                    <input
                      type="text"
                      id="destination"
                      name="destination"
                      value={formData.destination}
                      onChange={handleChange}
                      required
                      className="w-full px-3 md:px-4 py-2.5 md:py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent text-sm transition-all"
                      placeholder="Ex: Dakar, Sénégal"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="volume" className="block text-xs md:text-sm font-semibold text-gray-700 mb-2">
                    Volume / Poids Estimé *
                  </label>
                  <input
                    type="text"
                    id="volume"
                    name="volume"
                    value={formData.volume}
                    onChange={handleChange}
                    required
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent text-sm transition-all"
                    placeholder="Ex: 500 kg ou 2 m³"
                  />
                </div>

                <div>
                  <label htmlFor="description" className="block text-xs md:text-sm font-semibold text-gray-700 mb-2">
                    Description de la Marchandise *
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    rows={4}
                    maxLength={500}
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent text-sm resize-none transition-all"
                    placeholder="Décrivez votre marchandise : nature des produits, dimensions, conditionnement, valeur approximative..."
                  ></textarea>
                  <div className="text-xs text-gray-500 mt-1 text-right">
                    {formData.description.length}/500 caractères
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-accent hover:bg-accent-dark text-white py-3 md:py-4 rounded-lg text-sm md:text-base font-semibold transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill"></i>
                      Demander un Devis Gratuit
                    </>
                  )}
                </button>

                {submitStatus === 'success' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-xs md:text-sm flex items-start gap-3"
                  >
                    <i className="ri-checkbox-circle-fill text-lg md:text-xl flex-shrink-0"></i>
                    <div>
                      <strong>Demande envoyée avec succès !</strong>
                      <p className="mt-1">Merci pour votre demande. Notre équipe vous répondra sous 24h avec un devis détaillé.</p>
                    </div>
                  </motion.div>
                )}

                {submitStatus === 'error' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-xs md:text-sm flex items-start gap-3"
                  >
                    <i className="ri-error-warning-fill text-lg md:text-xl flex-shrink-0"></i>
                    <div>
                      <strong>Erreur d'envoi</strong>
                      <p className="mt-1">Une erreur est survenue. Veuillez réessayer ou nous contacter directement par email.</p>
                    </div>
                  </motion.div>
                )}
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default DevisSection;
